import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FileText, FilePdf, FileCsv, File as FileIcon, X, DownloadSimple } from "@phosphor-icons/react";
import { fileUrl } from "../lib/api";
import { ImagePreview } from "./ImagePreview";

const docIcon = (mime, size = 20) => {
  if (mime === "application/pdf") return <FilePdf size={size} weight="duotone" />;
  if (mime?.includes("csv")) return <FileCsv size={size} weight="duotone" />;
  if (mime?.startsWith("text/") || mime?.includes("json") || mime?.includes("word")) return <FileText size={size} weight="duotone" />;
  return <FileIcon size={size} weight="duotone" />;
};

const DocPreview = ({ url, name, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 scale-in"
      data-testid="attachment-preview-modal" onClick={onClose}>
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <a href={url} download={name} data-testid="preview-download-button" title="Download" onClick={(e) => e.stopPropagation()}
          className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition-colors duration-200">
          <DownloadSimple size={20} />
        </a>
        <button onClick={onClose} data-testid="preview-close-button" title="Close (Esc)"
          className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition-colors duration-200">
          <X size={20} />
        </button>
      </div>
      <div className="w-full max-w-4xl h-[85vh] rounded-xl overflow-hidden bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <iframe src={url} title={name} className="w-full h-full border-0" />
      </div>
    </div>,
    document.body
  );
};

export const AttachmentCard = ({ att, onRemove, compact }) => {
  const [preview, setPreview] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  const isImage = att.mime?.startsWith("image/");
  const url = fileUrl(att.id);
  const showImg = isImage && !imgErr;

  return (
    <>
      <div data-testid={`attachment-card-${att.id}`}
        className={`group/att relative flex items-center gap-2.5 bg-[var(--surface)] border border-[var(--border-strong)] rounded-xl overflow-hidden cursor-pointer hover:border-[var(--accent)]/60 transition-colors duration-300 ${compact ? "p-1.5 pr-3" : "p-2 pr-3.5"}`}
        onClick={() => setPreview(true)} title="Click to preview">
        {showImg ? (
          <img src={url} alt={att.name} onError={() => setImgErr(true)}
               className={`object-cover rounded-lg ${compact ? "w-10 h-10" : "w-12 h-12"}`} />
        ) : (
          <span className={`flex items-center justify-center rounded-lg bg-[var(--surface-2)] text-[var(--accent)] ${compact ? "w-10 h-10" : "w-12 h-12"}`}>
            {docIcon(att.mime, compact ? 18 : 22)}
          </span>
        )}
        <div className="min-w-0">
          <p className="text-xs font-medium text-[var(--text)] truncate max-w-[140px]">{att.name}</p>
          <p className="text-[10px] text-[var(--muted)] uppercase">{att.mime?.split("/")[1]?.slice(0, 12) || "file"}</p>
        </div>
        {onRemove && (
          <button data-testid={`remove-attachment-${att.id}`}
            onClick={(e) => { e.stopPropagation(); onRemove(att.id); }}
            className="absolute -top-0 -right-0 m-1 bg-[var(--surface)] border border-[var(--border-strong)] rounded-full p-0.5 text-[var(--muted)] hover:text-[var(--error)] opacity-0 group-hover/att:opacity-100 transition-opacity duration-300">
            <X size={11} weight="bold" />
          </button>
        )}
      </div>

      {preview && (showImg
        ? <ImagePreview url={url} name={att.name} onClose={() => setPreview(false)} sourceType="file" sourceId={att.id} />
        : <DocPreview url={url} name={att.name} onClose={() => setPreview(false)} />)}
    </>
  );
};
